import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { ShieldCheck, Download, Search, Send, CheckCircle2, XCircle, Users, Wrench } from "lucide-react";

const fmt = (s) => { if (!s) return "—"; try { return new Date(s).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" }); } catch { return s; } };

export default function Privacy() {
  const [data, setData] = useState({ items: [], riepilogo: { totale: 0, sottoscritti: 0, mancanti: 0 } });
  const [loading, setLoading] = useState(true);
  const [role, setRole] = useState("");
  const [stato, setStato] = useState("");
  const [condominioId, setCondominioId] = useState("");
  const [categoria, setCategoria] = useState("");
  const [q, setQ] = useState("");
  const [condomini, setCondomini] = useState([]);
  const [selected, setSelected] = useState({});
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/privacy/consensi", { params: { role, stato, condominio_id: condominioId, categoria, q } });
      setData(data);
      setSelected({});
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore caricamento"); }
    finally { setLoading(false); }
  }, [role, stato, condominioId, categoria, q]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/condomini").then((r) => setCondomini(r.data || [])).catch(() => {}); }, []);

  const downloadPdf = async (it) => {
    try {
      const res = await api.get(`/privacy/consensi/${it.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "PDF non disponibile"); }
  };

  const selectableIds = data.items.filter((i) => i.puo_sollecitare).map((i) => i.id);
  const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selected[id]);
  const toggleAll = () => {
    if (allSelected) setSelected({});
    else { const s = {}; selectableIds.forEach((id) => (s[id] = true)); setSelected(s); }
  };
  const selCount = Object.values(selected).filter(Boolean).length;

  const sollecita = async (mode) => {
    setSending(true);
    try {
      const payload = mode === "selezionati"
        ? { user_ids: Object.keys(selected).filter((k) => selected[k]) }
        : { role, categoria, condominio_id: condominioId };
      const { data } = await api.post("/privacy/solleciti", payload);
      if (data.inviati > 0) toast.success(data.message);
      else toast.info(data.message);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Invio solleciti non riuscito"); }
    finally { setSending(false); }
  };

  const r = data.riepilogo;

  return (
    <div className="space-y-5" data-testid="privacy-page">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-brand flex items-center justify-center"><ShieldCheck className="w-6 h-6 text-white" /></div>
        <div>
          <h1 className="font-display text-3xl font-bold text-brand">Privacy</h1>
          <p className="text-base text-slate-600">Archivio dei consensi GDPR di condòmini e fornitori e sollecito dei mancanti.</p>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <Card className="p-4"><div className="text-sm text-slate-500">Totale</div><div className="text-2xl font-bold text-slate-800" data-testid="privacy-tot">{r.totale}</div></Card>
        <Card className="p-4"><div className="text-sm text-slate-500">Consenso sottoscritto</div><div className="text-2xl font-bold text-green-700" data-testid="privacy-ok">{r.sottoscritti}</div></Card>
        <Card className="p-4"><div className="text-sm text-slate-500">Consenso mancante</div><div className="text-2xl font-bold text-red-700" data-testid="privacy-missing">{r.mancanti}</div></Card>
      </div>

      <Card className="p-4">
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <div>
            <label className="text-xs font-bold text-slate-500">Ruolo</label>
            <select data-testid="privacy-filter-role" value={role} onChange={(e) => setRole(e.target.value)} className="h-10 mt-1 w-full border-2 border-slate-200 rounded-lg px-2 bg-white text-sm">
              <option value="">Tutti</option><option value="condomino">Condòmini</option><option value="fornitore">Fornitori</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-bold text-slate-500">Stato consenso</label>
            <select data-testid="privacy-filter-stato" value={stato} onChange={(e) => setStato(e.target.value)} className="h-10 mt-1 w-full border-2 border-slate-200 rounded-lg px-2 bg-white text-sm">
              <option value="">Tutti</option><option value="ok">Sottoscritto</option><option value="mancante">Mancante</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-bold text-slate-500">Condominio</label>
            <select data-testid="privacy-filter-cond" value={condominioId} onChange={(e) => setCondominioId(e.target.value)} className="h-10 mt-1 w-full border-2 border-slate-200 rounded-lg px-2 bg-white text-sm">
              <option value="">Tutti</option>
              {condomini.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-bold text-slate-500">Categoria fornitore</label>
            <Input data-testid="privacy-filter-cat" value={categoria} onChange={(e) => setCategoria(e.target.value)} placeholder="es. Idraulico" className="h-10 mt-1" />
          </div>
          <div>
            <label className="text-xs font-bold text-slate-500">Cerca</label>
            <div className="relative mt-1">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input data-testid="privacy-filter-q" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nome o email" className="h-10 pl-9" />
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 mt-3">
          <Button data-testid="privacy-sollecita-sel" onClick={() => sollecita("selezionati")} disabled={sending || selCount === 0} className="bg-brand hover:bg-brand-dark">
            <Send className="w-4 h-4 mr-2" /> Sollecita selezionati ({selCount})
          </Button>
          <Button data-testid="privacy-sollecita-mancanti" onClick={() => sollecita("mancanti")} disabled={sending} variant="outline">
            <Send className="w-4 h-4 mr-2" /> Sollecita tutti i mancanti (filtri attivi)
          </Button>
        </div>
      </Card>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#FBEEF3] text-brand">
              <tr>
                <th className="p-3 text-left w-10"><input type="checkbox" data-testid="privacy-select-all" checked={allSelected} onChange={toggleAll} /></th>
                <th className="p-3 text-left">Nominativo</th>
                <th className="p-3 text-left">Tipo</th>
                <th className="p-3 text-left">Riferimento</th>
                <th className="p-3 text-left">Consenso</th>
                <th className="p-3 text-left">Data / Versione</th>
                <th className="p-3 text-right">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="p-6 text-center text-slate-400">Caricamento...</td></tr>
              ) : data.items.length === 0 ? (
                <tr><td colSpan={7} className="p-6 text-center text-slate-400">Nessun risultato con i filtri attivi.</td></tr>
              ) : data.items.map((it) => (
                <tr key={it.id} className="border-t border-slate-100" data-testid={`privacy-row-${it.id}`}>
                  <td className="p-3">
                    {it.puo_sollecitare && (
                      <input type="checkbox" data-testid={`privacy-check-${it.id}`} checked={!!selected[it.id]}
                        onChange={(e) => setSelected((s) => ({ ...s, [it.id]: e.target.checked }))} />
                    )}
                  </td>
                  <td className="p-3">
                    <div className="font-semibold text-slate-800">{it.name}</div>
                    <div className="text-slate-500 text-xs">{it.email || "— nessuna email —"}</div>
                  </td>
                  <td className="p-3">
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-slate-600">
                      {it.role === "fornitore" ? <Wrench className="w-3.5 h-3.5" /> : <Users className="w-3.5 h-3.5" />}
                      {it.role === "fornitore" ? "Fornitore" : "Condòmino"}
                    </span>
                  </td>
                  <td className="p-3 text-slate-600">{it.role === "fornitore" ? (it.categorie || []).join(", ") || "—" : (it.condominio_nome || "—")}</td>
                  <td className="p-3">
                    {it.privacy_consenso
                      ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-100 text-green-800 text-xs font-bold"><CheckCircle2 className="w-3.5 h-3.5" /> Sottoscritto</span>
                      : <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-xs font-bold"><XCircle className="w-3.5 h-3.5" /> Mancante</span>}
                  </td>
                  <td className="p-3 text-slate-600">{it.privacy_consenso ? <>{fmt(it.privacy_consenso_at)}<div className="text-xs text-slate-400">v. {it.privacy_versione || "—"}</div></> : "—"}</td>
                  <td className="p-3 text-right">
                    {it.privacy_consenso && (
                      <Button size="sm" variant="outline" data-testid={`privacy-pdf-${it.id}`} onClick={() => downloadPdf(it)}>
                        <Download className="w-4 h-4 mr-1.5" /> PDF
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
